import { type JSX, useEffect, useRef, useState } from "react";

/**
 * One numbered command in the agent install sequence, styled as a terminal so it reads as
 * something you run rather than something you read. Ported from science-ui's InstructionStep.
 *
 * ⛔⭐ `command` must NEVER carry the plaintext credential. science-ui's own step 2 is
 * `configure --server X --token Y`, which puts the value in argv, where `ps` shows it to every
 * other user on a shared login node for as long as the command runs. Our agent reads it from
 * stdin precisely to close that window (`agent/foundry_agent/cli.py`, `_read_shared_value`), so
 * copying science-ui literally here would reopen it. Spec decision 6.
 */
export function InstructionStep({
  step,
  title,
  description,
  command,
}: {
  step: number;
  title: string;
  description: string;
  command: string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  // ⚠️ Cleared on unmount. The drawer can close within the confirmation window, and a timeout
  // that fires afterwards sets state on a component that is gone.
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = (): void => {
    void navigator.clipboard?.writeText(command);
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <p className="text-xs text-gray-700">
        <span className="font-semibold">Step {step}</span> {title}
      </p>
      <p className="mb-1 text-xs text-gray-500">{description}</p>
      <div className="flex items-center overflow-hidden rounded-lg bg-gray-900">
        <code className="flex-1 select-all overflow-x-auto px-3 py-1.5 font-mono text-xs text-white">
          {command}
        </code>
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy step ${step} command`}
          className="shrink-0 px-2 py-1.5 text-xs text-gray-400 hover:text-white">
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
